import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Carousel, Embla, useAnimationOffsetEffect } from '@mantine/carousel'
import {
  MantineProvider,
  Modal,
  ModalBaseStylesNames,
  Styles,
  Text,
  useMantineTheme,
} from '@mantine/core'
import React, { useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'

import i18n from '../i18n'
import {
  InnerPreviewContentProps,
  StaticAssetPreviewProps,
} from '../types/preview'
import Asset from './Asset'

const TRANSITION_DURATION = 50

const StaticAssetPreview = ({
  onDelete,
  assets,
  maxWidth,
  height,
  carouselOverwriteProps = {},
  mantineThemeOverride = {},
  modalOverwriteProps = {},
}: StaticAssetPreviewProps): React.ReactElement => {
  const [embla, setEmbla] = useState<Embla | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useAnimationOffsetEffect(embla, TRANSITION_DURATION)

  const handleModalClose = (): void => {
    setOpenIndex(null)
    setModalOpen(false)
  }

  const handleAssetClick = (index: number): void => {
    if (modalOpen) {
      return
    } else {
      setOpenIndex(index)
    }
  }

  useEffect(() => {
    if (typeof openIndex === 'number') {
      setModalOpen(true)
    }
  }, [openIndex])

  useEffect(() => {
    if (assets.length === 0) {
      handleModalClose()
    }
  }, [assets.length])

  if (assets.length === 0) {
    return <></>
  }

  return modalOpen ? (
    <MantineProvider theme={mantineThemeOverride}>
      <I18nextProvider i18n={i18n}>
        <Modal
          opened={modalOpen}
          size="90vw"
          padding={0}
          withCloseButton={false}
          onClose={handleModalClose}
          transitionProps={{
            duration: TRANSITION_DURATION,
          }}
          {...modalOverwriteProps}
          styles={{
            ...(modalOverwriteProps?.styles ?? {}),
            content: {
              ...(modalOverwriteProps?.styles?.[
                'content' as keyof Styles<ModalBaseStylesNames>
              ] ?? {}),
              backgroundColor: 'transparent',
              boxShadow: 'none',
              border: 'none',
            },
          }}
        >
          <div style={{ width: '90vw', position: 'relative' }}>
            <InnerPreviewContent
              assets={assets}
              maxWidth="90vw"
              height="100%"
              onDelete={onDelete}
              setEmbla={setEmbla}
              openModal={handleAssetClick}
              modalOpen={modalOpen}
              carouselOverwriteProps={carouselOverwriteProps}
              initialSlide={openIndex}
            />

            {typeof openIndex === 'number' && assets[openIndex] && (
              <Text
                style={{
                  cursor: 'pointer',
                  color: 'white',
                  top: 0,
                  left: 0,
                  position: 'absolute',
                }}
              >
                {assets[openIndex].name}
              </Text>
            )}

            <FontAwesomeIcon
              onClick={handleModalClose}
              icon={faTimes}
              style={{
                fontSize: 20,
                cursor: 'pointer',
                color: 'white',
                top: 0,
                right: 0,
                position: 'absolute',
              }}
            />
          </div>
        </Modal>
      </I18nextProvider>
    </MantineProvider>
  ) : (
    <MantineProvider theme={mantineThemeOverride}>
      <I18nextProvider i18n={i18n}>
        <InnerPreviewContent
          assets={assets}
          maxWidth={maxWidth}
          height={height}
          onDelete={onDelete}
          setEmbla={setEmbla}
          openModal={handleAssetClick}
          modalOpen={modalOpen}
          carouselOverwriteProps={carouselOverwriteProps}
          initialSlide={openIndex}
        />
      </I18nextProvider>
    </MantineProvider>
  )
}

export default StaticAssetPreview

const InnerPreviewContent = ({
  height,
  assets,
  maxWidth,
  onDelete,
  setEmbla,
  openModal,
  modalOpen,
  carouselOverwriteProps,
  initialSlide,
}: InnerPreviewContentProps): React.ReactElement => {
  const theme = useMantineTheme()
  return (
    <Carousel
      withIndicators
      align="center"
      slideSize="33.333333%"
      slideGap="xs"
      breakpoints={[
        { maxWidth: 'md', slideSize: '50%' },
        { maxWidth: 'sm', slideSize: '100%', slideGap: 0 },
      ]}
      mx="auto"
      loop
      nextControlIcon={<FontAwesomeIcon icon={faChevronRight} />}
      styles={{
        container: {
          width: '100%',
        },
        control: {
          color: theme.colors[theme.primaryColor][6],
          borderColor: theme.colors[theme.primaryColor][6],
          backgroundColor: theme.colors[theme.primaryColor][0],
        },
      }}
      previousControlIcon={<FontAwesomeIcon icon={faChevronLeft} />}
      {...carouselOverwriteProps}
      initialSlide={initialSlide ?? undefined}
      height={height}
      getEmblaApi={setEmbla}
    >
      {assets.map((asset, index) => (
        <Carousel.Slide key={asset.id}>
          <Asset
            asset={asset}
            maxWidth={maxWidth}
            height={height}
            onDelete={onDelete}
            index={index}
            openModal={openModal}
            modalOpen={modalOpen}
          />
        </Carousel.Slide>
      ))}
    </Carousel>
  )
}
